import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import { XMarkIcon } from "@heroicons/react/24/outline";

import { notificationTypeOptions } from "./data";
import CustomInput from "../../../../common/CustomInput";
import Button from "../../../../common/Button";
import {
  countryOptions,
  designationOptions,
  physicianOptions,
  stateOptions,
} from "../../../create/options";
import {
  isNameError,
  isFieldWithLengthError,
} from "../../../../../helpers/input-validations";
import CommonSlider from "../../../../common/CommonSlider";

const NotificationCreate = ({
  type,
  updateType,
  open,
  onClose,
  onSave,
  onUpdate,
  existingNotification,
  create = false,
  notificationOptions = notificationTypeOptions,
}) => {
  const dispatch = useDispatch();
  const { serviceId } = useParams();
  // selection
  const [notificationId, setNotificationId] = useState(null);
  const [notificationType, setNotificationType] = useState("");
  const [firstName, setFirstName] = useState(null);
  const [middleName, setMiddleName] = useState(null);
  const [lastName, setLastName] = useState(null);
  const [deptOrClinicName, setDeptOrClinicName] = useState(null);
  const [licenseNumber, setLicenseNumber] = useState(null);
  const [designation, setDesignation] = useState(null);
  const [isPrimaryPhysician, setIsPrimaryPhysician] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [fax, setFax] = useState(null);
  const [emailAddress, setEmailAddress] = useState(null);
  const [address, setAddress] = useState(null);
  const [unitNumber, setUnitNumber] = useState(null);
  const [poBox, setPOBox] = useState(null);
  const [city, setCity] = useState(null);
  const [province, setProvince] = useState(null);
  const [postalCode, setPostalCode] = useState(null);
  const [country, setCountry] = useState(null);
  const [comments, setComments] = useState(null);

  const [notificationTypeError, setNotificationTypeError] = useState(true);
  const [deptOrClinicNameError, setDeptOrClinicNameError] = useState(true);
  const [lastNameError, setLastNameError] = useState(false);
  const [firstNameError, setFirstNameError] = useState(false);
  const [licenseNumberError, setLicenseNumberError] = useState(false);

  const fieldsToCheck = [
    {
      field: notificationType,
      checkFn: isNameError,
      setErrorFn: setNotificationTypeError,
      alter: false,
      name: "notificationType",
    },
    {
      field: deptOrClinicName,
      checkFn: isNameError,
      setErrorFn: setDeptOrClinicNameError,
      alter: false,
      name: "deptOrClinicName",
    },
    {
      field: lastName,
      checkFn: isNameError,
      setErrorFn: setLastNameError,
      alter: false,
      name: "lastName",
    },
    {
      field: firstName,
      checkFn: isNameError,
      setErrorFn: setFirstNameError,
      alter: false,
      name: "firstName",
    },
  ];

  const [allowSubmit, setAllowSubmit] = useState(false);
  const [optionalPhysician, setOptionalPhysician] = useState(false);

  useEffect(() => {
    if (type?.length > 0) {
      setNotificationType(type);
    }
  }, [type, open]);

  useEffect(() => {
    if (
      notificationType === "Emergency - No Primary Provider" ||
      notificationType === "Walk In Clinic - No Primary Provider"
    )
      setOptionalPhysician(true);
    else setOptionalPhysician(false);
  }, [notificationType]);

  useEffect(() => {
    getFieldsToCheck().forEach(({ field, checkFn, setErrorFn, alter }) => {
      if (field === null) return;
      const hasError = checkFn.length !== 2 && checkFn(field);
      setErrorFn(hasError);
    });
  }, [...fieldsToCheck.map(({ field }) => field)]);

  useEffect(() => {
    if (
      !optionalPhysician &&
      !notificationTypeError &&
      !lastNameError &&
      !firstNameError &&
      !licenseNumberError
    ) {
      setAllowSubmit(true);
    } else if (
      optionalPhysician &&
      !notificationTypeError &&
      !deptOrClinicNameError
    ) {
      setAllowSubmit(true);
    } else {
      setAllowSubmit(false);
    }
  }, [notificationType, firstName, lastName, licenseNumber, deptOrClinicName]);

  useEffect(() => {
    if (!create && existingNotification) {
      setNotificationId(existingNotification.referralId);
      setNotificationType(existingNotification.referralType);
      setDeptOrClinicName(existingNotification.clinicName);
      setFirstName(existingNotification.firstName);
      setMiddleName(existingNotification.middleName);
      setLastName(existingNotification.lastName);
      setLicenseNumber(existingNotification.licenseNumber);
      setDesignation(existingNotification.designation);
      setIsPrimaryPhysician(
        existingNotification.isPrimaryPhysician ? "primary" : ""
      );
      setPhoneNumber(existingNotification.phoneNumber);
      setFax(existingNotification.fax);
      setEmailAddress(existingNotification.emailAddress);
      setAddress(existingNotification.address);
      setUnitNumber(existingNotification.unitNumber);
      setPOBox(existingNotification.poBox);
      setCity(existingNotification.city);
      setProvince(existingNotification.province);
      setPostalCode(existingNotification.postalCode);
      setCountry(existingNotification.country);
      setComments(existingNotification.comments);

      setAllowSubmit(true);
    } else {
      setNotificationId(null);
      setDeptOrClinicName(null);
      setFirstName(null);
      setMiddleName(null);
      setLastName(null);
      setLicenseNumber(null);
      setDesignation(null);
      setIsPrimaryPhysician("");
      setPhoneNumber(null);
      setFax(null);
      setEmailAddress(null);
      setAddress(null);
      setUnitNumber(null);
      setPOBox(null);
      setCity(null);
      setProvince(null);
      setPostalCode(null);
      setCountry(null);
      setComments(null);
      setLastNameError(false);
      setFirstNameError(false);
      setLicenseNumberError(false);
      setDeptOrClinicNameError(false);
    }
  }, [existingNotification, open, create]);

  const handleSave = () => {
    let allFieldsValid = true;
    let fieldsToValidate = getFieldsToCheck();
    fieldsToValidate
      .filter(
        (x) => optionalPhysician === true && x.name === "notificationType"
      )
      .forEach(({ field, checkFn, setErrorFn }) => {
        const hasError = checkFn.length !== 2 && checkFn(field);
        setErrorFn(hasError);
        if (hasError) {
          allFieldsValid = false;
        }
      });

    if (allFieldsValid) {
      const currentNotification = {
        referralId: notificationId,
        referralType: notificationType,
        clinicName: deptOrClinicName,
        firstName: firstName,
        lastName: lastName,
        middleName: middleName,
        licenseNumber: licenseNumber,
        designation: designation,
        isPrimaryPhysician: isPrimaryPhysician === "primary",
        phoneNumber: phoneNumber,
        fax: fax,
        emailAddress: emailAddress,
        address: address,
        unitNumber: unitNumber,
        poBox: poBox,
        city: city,
        province: province,
        postalCode: postalCode,
        country: country,
        comments: comments,
      };

      if (create) onSave(currentNotification);
      else onUpdate(currentNotification);
    }
  };

  const getFieldsToCheck = () => {
    if (optionalPhysician)
      return fieldsToCheck.filter(
        (x) => x.name === "notificationType" || x.name === "deptOrClinicName"
      );
    else return fieldsToCheck;
  };

  return (
    <CommonSlider open={open} onClose={onClose}>
      <div className="flex flex-col flex-1 h-full overflow-y-auto ">
        <div className="relative flex flex-col flex-1">
          {/* Header */}
          <div className="px-8 py-6 ">
            <div className="flex items-start justify-between space-x-3">
              <div className="flex flex-col">
                <p className="font-bold text-gray-900 text-display-xs">
                  {create ? "Confirm" : "Edit"} {optionalPhysician ? "Contact" : "Notification"} Information
                </p>
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                  onClick={() => onClose(false)}
                >
                  <span className="sr-only">Close panel</span>
                  <XMarkIcon
                    className="w-5 h-5 m-auto"
                    strokeWidth="2"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>
          <div className="flex flex-col justify-between flex-1 px-8 h-full overflow-y-auto">
            <div className="grid grid-cols-12 gap-3 h-[500px]">
              <CustomInput
                options={[
                  { value: "", label: "Select Notification Type" },
                  ...notificationOptions,
                ]}
                value={notificationType}
                onChange={(e) => {
                  setNotificationType(e.value);
                }}
                name="notificationType"
                className="col-span-12"
                label="Notification Type"
                type="dropdown"
              />
              {notificationType === "Emergency - No Primary Provider" && (
                <CustomInput
                  name="deptName"
                  className="col-span-12"
                  label="Hospital - ER Dept Name"
                  placeholder="Enter Hospital - ER Dept Name"
                  type="text"
                  capitalizeFirstLetter={true}
                  value={deptOrClinicName}
                  onChange={(e) => setDeptOrClinicName(e.target.value)}
                  required
                />
              )}
              {notificationType === "Walk In Clinic - No Primary Provider" && (
                <CustomInput
                  name="clinicName"
                  className="col-span-12"
                  label="Walk In Clinic Name"
                  placeholder="Enter Walk In Clinic Name"
                  type="text"
                  capitalizeFirstLetter={true}
                  value={deptOrClinicName}
                  onChange={(e) => setDeptOrClinicName(e.target.value)}
                  required
                />
              )}
              {(notificationType === "Emergency - No Primary Provider" ||
                notificationType ===
                "Walk In Clinic - No Primary Provider") && (
                  <p className="col-span-12 font-medium text-gray-900 text-body-md">
                    Physician Information (Optional)
                  </p>
                )}
              <CustomInput
                name="lastName"
                className="col-span-4"
                label="Last Name"
                placeholder="Enter last name"
                type="text"
                capitalizeFirstLetter={true}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                error={lastNameError}
                required={!optionalPhysician}
              />
              <CustomInput
                name="middleName"
                className="col-span-4"
                label="Middle Name"
                placeholder="Enter middle name"
                type="text"
                capitalizeFirstLetter={true}
                value={middleName}
                onChange={(e) => setMiddleName(e.target.value)}
              />
              <CustomInput
                name="firstName"
                className="col-span-4"
                label="First Name"
                placeholder="Enter first name"
                type="text"
                capitalizeFirstLetter={true}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                error={firstNameError}
                required={!optionalPhysician}
              />
              <CustomInput
                name="licenseNumber"
                className="col-span-6"
                label={"License Number "}
                placeholder="XXXXX"
                value={licenseNumber}
                error={licenseNumberError}
                onChange={(e) => {
                  setLicenseNumber(e.target.value);
                  setLicenseNumberError(
                    isFieldWithLengthError(e.target.value, 5)
                  );
                }}
                type="text"
              />
              <CustomInput
                options={designationOptions}
                name="designation"
                className="col-span-6"
                label="Designation"
                type="dropdown"
                value={designation}
                onChange={(e) => setDesignation(e.value)}
              />
              <p className="pl-2 pb-3 col-span-12 text-gray-700 text-body-xs">
                <span>
                  Search license number{" "}
                  <u>
                    <a target="_blank" href="https://www.cpso.on.ca">
                      here
                    </a>
                  </u>
                  .
                </span>
              </p>
              <CustomInput
                className="col-span-12"
                options={physicianOptions}
                type="radio"
                name="isPrimaryPhysician"
                value={isPrimaryPhysician}
                checked={isPrimaryPhysician}
                onChange={(e) => {
                  setIsPrimaryPhysician(e.target.value);
                }}
              />
              <div className="col-span-12 my-3 bg-gray-100 h-[1px] w-full " />
              <p className="col-span-12 font-medium text-gray-900 text-body-md">
                Contact Information
              </p>
              <CustomInput
                name="phoneNumber"
                className="col-span-6"
                label="Phone Number"
                placeholder="(555) 000-0000"
                type="phone"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
              <CustomInput
                name="fax"
                className="col-span-6"
                label="FAX"
                placeholder="Enter Fax"
                type="text"
                mask="(999) 999-9999"
                value={fax}
                onChange={(e) => setFax(e.target.value)}
              />
              <CustomInput
                name="emailAddress"
                className="col-span-12"
                label="Email Address"
                placeholder="Enter Email Address"
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
              />
              <CustomInput
                name="address"
                className="col-span-12"
                label="Address"
                placeholder=" Enter Street Number & Street Name"
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
              <CustomInput
                name="unitNumber"
                className="col-span-6"
                label="Unit Number"
                placeholder="Enter Unit Number"
                type="text"
                value={unitNumber}
                onChange={(e) => setUnitNumber(e.target.value)}
              />
              <CustomInput
                name="poBox"
                className="col-span-6"
                label="PO Box"
                placeholder="PO Box"
                type="age"
                value={poBox}
                onChange={(e) => setPOBox(e.target.value)}
              />
              <CustomInput
                name="city"
                className="col-span-6"
                label="City"
                placeholder="Enter City"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
              <CustomInput
                options={stateOptions}
                name="province"
                className="col-span-6"
                label="Province/State"
                type="dropdown"
                value={province}
                onChange={(e) => setProvince(e.value)}
              />
              <CustomInput
                name="postalCode"
                className="col-span-6"
                label="Postal Code"
                placeholder="Enter Postal Code"
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
              />
              <CustomInput
                options={countryOptions}
                name="country"
                className="col-span-6"
                label="Country"
                type="dropdown"
                value={country}
                onChange={(e) => setCountry(e.value)}
              />
              <div className="col-span-12 my-3 bg-gray-100 h-[1px] w-full " />
              <CustomInput
                name="comments"
                className="col-span-12"
                label="Comments"
                placeholder="Enter Note"
                type="text"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center justify-center py-6">
            <div className="flex items-center justify-center space-x-5">
              <Button
                className="min-w-[200px]"
                text={`Cancel`}
                type="secondary"
                onClick={() => onClose(false)}
              />
              <Button
                className="min-w-[200px]"
                text={create ? "Save" : "Update"}
                disabled={!allowSubmit}
                type="primary"
                onClick={() => {
                  handleSave();
                  updateType(notificationType);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </CommonSlider>
  );
};

export default NotificationCreate;
